import React, { useState } from 'react';
import { DEFAULT_LOGO } from '../hooks/useStorage';

// Supabase transformations are requested only for eligible public raster images.
// Keep the original URL as a fallback if transformations are unavailable.
const optimizedStorageUrl = (src, width) => {
  if (!src || !Number.isFinite(width) || width <= 0) return src;
  try {
    const url = new URL(src);
    if (
      !url.hostname.endsWith('.supabase.co') ||
      !url.pathname.startsWith('/storage/v1/object/public/') ||
      !/\.(jpe?g|png|webp)$/i.test(url.pathname)
    ) return src;
    url.pathname = url.pathname.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
    url.searchParams.set('width', String(Math.round(width)));
    url.searchParams.set('quality', '75');
    return url.toString();
  } catch {
    return src;
  }
};

const ImageWithFallback = ({
  src,
  alt,
  className = '',
  fallback = null,
  imageWidth,
  loading = 'lazy',
  decoding = 'async',
  ...props
}) => {
  const [failedSrc, setFailedSrc] = useState(null);
  const [forceOriginalSrc, setForceOriginalSrc] = useState(null);
  const [loadedSrc, setLoadedSrc] = useState(null);

  const cleanSrc = src
    ? src.replace(/\.png\.png$/, '.png').replace(/\.jpg\.jpg$/, '.jpg').replace(/\.jpeg\.jpeg$/, '.jpeg')
    : null;
  const transformedSrc = optimizedStorageUrl(cleanSrc, imageWidth);
  const useOriginal = forceOriginalSrc === cleanSrc;
  const finalSrc = useOriginal ? cleanSrc : transformedSrc;
  const error = !!cleanSrc && failedSrc === cleanSrc;

  if (!cleanSrc || error) {
    if (fallback) return fallback;
    if (className.includes('rounded-full')) {
      return (
        <div className={`bg-primary-600 flex items-center justify-center ${className}`}>
          <span className="text-white font-bold text-xl">ΑΟΧ</span>
        </div>
      );
    }
    return <img src={DEFAULT_LOGO} alt={alt || 'ΑΟΧ'} className={className} loading={loading} decoding={decoding} {...props} />;
  }

  return (
    <img
      src={finalSrc}
      alt={alt}
      className={className}
      loading={loading}
      decoding={decoding}
      onLoad={() => setLoadedSrc(finalSrc)}
      onError={() => {
        if (finalSrc !== cleanSrc) {
          // A transformed image may fail if image transformations are unavailable.
          setForceOriginalSrc(cleanSrc);
        } else {
          if (import.meta.env.DEV) console.warn('Image failed to load:', cleanSrc);
          setFailedSrc(cleanSrc);
        }
      }}
      style={loadedSrc !== finalSrc ? { opacity: 0.5 } : {}}
      {...props}
    />
  );
};

export default ImageWithFallback;
